import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { generateCompatibility } from '../utils/Compatibility'

const ORANGE = '#FF5722'
const BG = '#0D0D0D'
const CARD = '#141414'
const BORDER = '#1f1f1f'
const GREEN = '#4CAF50'

const foodEmojis = {
  Biryani: '🍚', Pizza: '🍕', Dosa: '🥘', Ramen: '🍜',
  Burger: '🍔', Sushi: '🍣', Tacos: '🌮', Shawarma: '🧆',
  'Butter Chicken': '🍛', Pasta: '🍝', Dessert: '🍦',
}

const AVATAR_COLORS = ['#E91E63', '#2196F3', '#9C27B0', '#FF9800', '#00BCD4', '#8BC34A']

function getInitials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || '?'
}

function scoreColor(s) {
  return s >= 85 ? GREEN : s >= 70 ? '#FFC107' : '#FF7043'
}

function BottomIcon({ id, active }) {
  const color = active ? ORANGE : '#444'

  if (id === 'home') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  if (id === 'discover') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24">
        <path fill={color} d="M7.2 4.5c.8 0 1.4.6 1.4 1.4v3.9l3.6 3.6 1.9-1.9-.9-.9a1.2 1.2 0 011.7-1.7l2.7 2.7a1.2 1.2 0 01-1.7 1.7l-.9-.9-1.9 1.9 3.6 3.6c.5.5.5 1.3 0 1.8l-.8.8-4.4-4.4-4.4 4.4-.8-.8a1.3 1.3 0 010-1.8l3.6-3.6-3.3-3.3V5.9c0-.8.6-1.4 1.4-1.4z"/>
      </svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M8.2 4.5v5.8l3.8 3.8M5.9 4.5h4.6M6.8 7h2.8M13.2 9.6l4 4M16.8 8.4l1.8 1.8M15.6 10.8L9 17.4M8.6 19.8L19.8 8.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  }
  if (id === 'matches') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24">
        <path fill={color} d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z"/>
      </svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  }
  return active ? (
    <svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill={color} /><path fill={color} d="M4 21a8 8 0 0116 0H4z" /></svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="7" r="4" stroke={color} strokeWidth="2" /></svg>
  )
}

export default function Discover({ session, onNavigate, defaultFilter }) {
  const [myCravings, setMyCravings] = useState(null)
  const [allCravings, setAllCravings] = useState([])
  const [activeFilter, setActiveFilter] = useState(defaultFilter || localStorage.getItem('discover_food_filter') || '')
  const [cardIndex, setCardIndex] = useState(0)
  const [loadingOthers, setLoadingOthers] = useState(true)

  // Swipe / compatibility state
  const [swiping, setSwiping] = useState(null)
  const [matchPopup, setMatchPopup] = useState(false)
  const [matchedProfile, setMatchedProfile] = useState(null)
  const [currentScore, setCurrentScore] = useState(null)
  const [currentSauceLine, setCurrentSauceLine] = useState(null)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    localStorage.removeItem('discover_food_filter')
    fetchMyCravings()
    fetchOthersCravings()
  }, []) // eslint-disable-line

  // Generate compatibility whenever the visible card changes
  useEffect(() => {
    const filteredCravings = activeFilter
      ? allCravings.filter(c => c.food_type === activeFilter)
      : allCravings
    const card = filteredCravings[cardIndex]
    if (!card || !session?.user?.email) return

    let cancelled = false
    setGenerating(true)
    setCurrentScore(null)
    setCurrentSauceLine(null)

    generateCompatibility(
      { full_name: session.user.email, area: 'Bangalore', food_type: card.food_type, time_slot: card.time_slot },
      { full_name: card.users?.full_name, area: card.users?.area, food_type: card.food_type, time_slot: card.time_slot }
    ).then(({ score, sauce_line }) => {
      if (cancelled) return
      setCurrentScore(score)
      setCurrentSauceLine(sauce_line)
    }).finally(() => {
      if (!cancelled) setGenerating(false)
    })

    return () => { cancelled = true }
  }, [cardIndex, allCravings, activeFilter]) // eslint-disable-line

  async function fetchMyCravings() {
    const { data } = await supabase
      .from('cravings')
      .select('id, food_type')
      .eq('user_id', session.user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false })

    const fetched = data || []
    const seen = new Set()
    const unique = fetched.filter(c => {
      if (seen.has(c.food_type)) return false
      seen.add(c.food_type)
      return true
    })
    setMyCravings(unique)

    if (unique.length === 1) {
      setActiveFilter(prev => prev || unique[0].food_type)
    }
  }

  async function fetchOthersCravings() {
    setLoadingOthers(true)
    const { data } = await supabase
      .from('cravings')
      .select('*, users (id, full_name, area)')
      .eq('is_active', true)
      .neq('user_id', session.user.id)
      .order('created_at', { ascending: false })

    setAllCravings(data || [])
    setLoadingOthers(false)
  }

  function handleFilterChange(value) {
    setActiveFilter(value)
    setCardIndex(0)
  }

  async function handleSwipe(direction, card) {
    if (!card) return
    setSwiping(direction)

    const { score, sauce_line } = await generateCompatibility(
      { full_name: session.user.email, area: 'Bangalore', food_type: card.food_type, time_slot: card.time_slot },
      { full_name: card.users?.full_name, area: card.users?.area, food_type: card.food_type, time_slot: card.time_slot }
    )

    await supabase.from('swipes').insert({
      swiper_id: session.user.id,
      swiped_id: card.user_id,
      craving_id: card.id,
      direction,
      compatibility_score: score,
      sauce_line,
    })

    if (direction === 'right') {
      const { data: existing } = await supabase
        .from('swipes')
        .select('*')
        .eq('swiper_id', card.user_id)
        .eq('swiped_id', session.user.id)
        .eq('direction', 'right')
        .single()

      if (existing) {
        await supabase.from('matches').insert({
          user_1_id: session.user.id,
          user_2_id: card.user_id,
          craving_id: card.id,
        })
        setTimeout(() => {
          setSwiping(null)
          setMatchedProfile(card)
          setMatchPopup(true)
        }, 400)
        return
      }
    }

    setTimeout(() => {
      setSwiping(null)
      setCardIndex(i => i + 1)
    }, 400)
  }

  const loading = myCravings === null || loadingOthers
  const noCravings = !loading && myCravings.length === 0

  const filteredCravings = activeFilter
    ? allCravings.filter(c => c.food_type === activeFilter)
    : allCravings

  const currentCard = filteredCravings[cardIndex]
  const totalCards = filteredCravings.length
  const avatarColor = AVATAR_COLORS[cardIndex % AVATAR_COLORS.length]

  return (
    <div style={{ minHeight: '100vh', background: BG, color: '#fff', fontFamily: "'DM Sans', sans-serif", paddingBottom: 92 }}>

      {/* Header */}
      <div style={{ padding: '52px 20px 12px', borderBottom: `1.5px solid ${BORDER}` }}>
        <div style={{ fontSize: 24, fontWeight: 800 }}>Discover tonight</div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Find cravings posted by others nearby</div>
      </div>

      {/* Filter pills — user's own active cravings only */}
      {!noCravings && (
        <div
          className="hide-scrollbar"
          style={{
            display: 'flex', gap: 8, overflowX: 'auto',
            padding: '12px 20px', borderBottom: `1.5px solid ${BORDER}`,
            scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}
        >
          {loading
            ? [1, 2, 3].map(i => (
                <div key={i} style={{
                  flexShrink: 0, width: 80, height: 34,
                  background: CARD, border: `1.5px solid ${BORDER}`,
                  borderRadius: 999, opacity: 0.4,
                }} />
              ))
            : myCravings.map(craving => {
                const isActive = activeFilter === craving.food_type
                return (
                  <button
                    key={craving.food_type}
                    onClick={() => handleFilterChange(craving.food_type)}
                    style={{
                      flexShrink: 0,
                      background: isActive ? `${ORANGE}22` : CARD,
                      border: `1.5px solid ${isActive ? ORANGE : BORDER}`,
                      color: isActive ? ORANGE : '#888',
                      borderRadius: 999, padding: '7px 14px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                    }}
                  >
                    {craving.food_type} {foodEmojis[craving.food_type] || '🍽️'}
                  </button>
                )
              })
          }
        </div>
      )}

      <div style={{ padding: 20, minHeight: 'calc(100vh - 250px)' }}>
        {loading ? (
          <div style={{ color: '#555', textAlign: 'center', paddingTop: 40, fontSize: 14 }}>
            Loading cravings...
          </div>

        ) : noCravings ? (
          /* Empty state */
          <div style={{ background: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 20, padding: '48px 24px', textAlign: 'center', marginTop: 20 }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🍽️</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>
              Post a craving first<br />to discover people tonight
            </div>
            <div style={{ color: '#555', fontSize: 13, marginTop: 8, marginBottom: 28 }}>
              Tell people what you're feeling and find your match.
            </div>
            <button
              onClick={() => onNavigate('post')}
              style={{
                background: ORANGE, border: 'none', color: '#fff',
                borderRadius: 14, padding: '14px 32px',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Post a craving 🔥
            </button>
          </div>

        ) : !currentCard ? (
          /* Exhausted all cards */
          <div style={{ background: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 20, padding: '36px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 42, marginBottom: 8 }}>🌙</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>No one else is craving this yet</div>
            <div style={{ color: '#555', fontSize: 13, marginTop: 6 }}>Check back soon — more people join every hour.</div>
          </div>

        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Counter */}
            <div style={{ textAlign: 'center', color: '#888', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
              {cardIndex + 1} of {totalCards} tonight
            </div>

            {/* Card */}
            <div
              style={{
                background: CARD,
                border: `1.5px solid ${avatarColor}40`,
                borderRadius: 24,
                overflow: 'hidden',
                transform: swiping === 'right'
                  ? 'translateX(110%) rotate(8deg)'
                  : swiping === 'left'
                    ? 'translateX(-110%) rotate(-8deg)'
                    : 'none',
                opacity: swiping ? 0 : 1,
                transition: 'all 0.35s ease',
              }}
            >
              {/* Gradient top band with avatar */}
              <div style={{
                background: `linear-gradient(135deg, ${avatarColor}25, ${BG})`,
                padding: '28px 24px 24px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: `${avatarColor}22`, border: `3px solid ${avatarColor}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: avatarColor, fontWeight: 700, fontSize: 28, letterSpacing: 1,
                }}>
                  {getInitials(currentCard.users?.full_name)}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                    {currentCard.users?.full_name || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                    📍 {currentCard.users?.area || currentCard.area}
                  </div>
                </div>
              </div>

              <div style={{ padding: '0 22px 24px' }}>
                {/* Compatibility score */}
                <div style={{
                  background: '#1a1a1a', borderRadius: 14, padding: '14px 16px',
                  marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 10, color: '#555', fontWeight: 700,
                      letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8,
                    }}>
                      Compatibility
                    </div>
                    <div style={{ height: 5, background: '#2a2a2a', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${currentScore || 0}%`,
                        background: scoreColor(currentScore || 75),
                        borderRadius: 3,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: scoreColor(currentScore || 75), minWidth: 54, textAlign: 'right' }}>
                    {generating ? '...' : `${currentScore}%`}
                  </div>
                </div>

                {/* Sauce line */}
                <div style={{
                  background: `${ORANGE}08`, border: `1.5px solid ${ORANGE}20`,
                  borderRadius: 12, padding: '12px 16px', marginBottom: 18,
                  fontSize: 13, color: '#bbb', fontStyle: 'italic', lineHeight: 1.6,
                }}>
                  🔥 {generating ? 'Reading your sauce...' : currentSauceLine}
                </div>

                {/* Craving tags */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{
                    fontSize: 10, color: '#444', fontWeight: 700,
                    letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10,
                  }}>
                    Tonight's craving
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      `🍽️ ${currentCard.food_type}`,
                      currentCard.time_slot ? `🕗 ${currentCard.time_slot}` : null,
                      currentCard.area,
                      currentCard.restaurant_suggestion,
                    ].filter(Boolean).map((tag, i) => (
                      <span key={i} style={{
                        background: '#1e1e1e', color: '#888',
                        borderRadius: 8, padding: '5px 10px',
                        fontSize: 12, fontWeight: 500,
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 12, padding: '0 22px 22px' }}>
                <button
                  onClick={() => handleSwipe('left', currentCard)}
                  style={{
                    flex: 1, height: 52, background: '#101010',
                    border: `1.5px solid ${BORDER}`, color: '#999',
                    borderRadius: 14, cursor: 'pointer',
                    fontSize: 22, fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  ✕
                </button>
                <button
                  onClick={() => handleSwipe('right', currentCard)}
                  style={{
                    flex: 2, height: 52, background: ORANGE,
                    border: 'none', color: '#fff',
                    borderRadius: 14, cursor: 'pointer',
                    fontSize: 16, fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Join 🔥
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', background: '#0a0a0a', borderTop: `1.5px solid ${BORDER}`,
        display: 'flex', padding: '10px 0 20px', zIndex: 100,
      }}>
        {[
          { label: 'Home', id: 'home' },
          { label: 'Discover', id: 'discover' },
          { label: 'Matches', id: 'matches' },
          { label: 'Profile', id: 'profile' },
        ].map(nav => (
          <div
            key={nav.id}
            onClick={() => onNavigate(nav.id)}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 0' }}
          >
            <BottomIcon id={nav.id} active={nav.id === 'discover'} />
            <span style={{ fontSize: 10, fontWeight: 500, color: nav.id === 'discover' ? ORANGE : '#444', letterSpacing: 0.3 }}>
              {nav.label}
            </span>
          </div>
        ))}
      </div>

      {/* Match popup */}
      {matchPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: '#000000ee',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', zIndex: 200, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🍳</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 8, letterSpacing: '-0.5px' }}>
            You just got Sauced.
          </div>
          <div style={{ fontSize: 15, color: '#666', marginBottom: 40, lineHeight: 1.6 }}>
            You and {matchedProfile?.users?.full_name} are both in.
            <br />Time to make a plan.
          </div>
          <button
            onClick={() => { setMatchPopup(false); onNavigate('matches') }}
            style={{
              background: ORANGE, color: '#fff', border: 'none',
              borderRadius: 14, padding: '15px 40px', fontSize: 16,
              fontWeight: 700, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              marginBottom: 16, width: '100%', maxWidth: 320,
            }}
          >
            Start chatting 💬
          </button>
          <div
            onClick={() => { setMatchPopup(false); setCardIndex(i => i + 1) }}
            style={{ color: '#444', fontSize: 13, cursor: 'pointer' }}
          >
            Keep swiping
          </div>
        </div>
      )}
    </div>
  )
}
